import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import {CALL_BUILT_IN_BACKGROUNDS} from '@toolify/client/src/modules/call/constants/callBuiltInBackgrounds'
import {downscaleImageToBlob} from '@toolify/client/src/modules/call/utils/downscaleImageToBlob'
import {
  ICallBuiltInBackground,
  ICallGradientSpec,
  ICallSavedBackground,
} from '@toolify/client/src/modules/call/types/ICallBackground'
import {useCallBackgroundApiStore} from '@toolify/client/src/stores/api/CallBackgroundApiStore/useCallBackgroundApiStore'
import {useFileUpload} from '@toolify/client/src/modules/files/upload/useFileUpload'
import {FileUploadTargetType} from '@toolify/client/src/modules/files/upload/types/FileUploadTargetType'
import {
  ICallBackgroundResponseInstance,
} from '@toolify/server/src/controllers/CallBackgroundController/types/ICallBackgroundResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'

function drawGradientToCanvas(gradient: ICallGradientSpec, width: number, height: number): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  if (!context) {
    return null
  }

  const angleRad = (gradient.angle * Math.PI) / 180
  const directionX = Math.sin(angleRad)
  const directionY = -Math.cos(angleRad)
  const lineLength = Math.abs(width * directionX) + Math.abs(height * directionY)
  const centerX = width / 2
  const centerY = height / 2
  const startX = centerX - (directionX * lineLength) / 2
  const startY = centerY - (directionY * lineLength) / 2
  const endX = centerX + (directionX * lineLength) / 2
  const endY = centerY + (directionY * lineLength) / 2

  const canvasGradient = context.createLinearGradient(startX, startY, endX, endY)
  const sortedStops = [...gradient.stops].sort((left, right) => left.offset - right.offset)
  for (const stop of sortedStops) {
    canvasGradient.addColorStop(Math.max(0, Math.min(1, stop.offset / 100)), stop.color)
  }
  context.fillStyle = canvasGradient
  context.fillRect(0, 0, width, height)
  return canvas
}

export const useCallBackgroundStore = defineStore('callBackground', () => {
  const savedBackgrounds = ref<ICallSavedBackground[]>([])
  const isLoadingSaved = ref(false)
  const hasLoadedSaved = ref(false)

  const gradientUrlCache = new Map<string, string>()

  const builtInGradients = computed(() => {
    return CALL_BUILT_IN_BACKGROUNDS.filter((item): item is ICallBuiltInBackground & {gradient: ICallGradientSpec} => {
      return item.kind === 'gradient' && Boolean(item.gradient)
    })
  })
  const builtInPhotos = computed(() => {
    return CALL_BUILT_IN_BACKGROUNDS.filter((item): item is ICallBuiltInBackground & {url: string} => {
      return item.kind === 'photo' && Boolean(item.url)
    })
  })

  async function loadSavedBackgrounds(): Promise<void> {
    if (isLoadingSaved.value) {
      return
    }
    isLoadingSaved.value = true
    try {
      const apiStore = useCallBackgroundApiStore()
      const backgrounds = await apiStore.listBackgrounds()
      savedBackgrounds.value = backgrounds.filter((background): background is typeof background & {url: string} => {
        return Boolean(background.url)
      })
      hasLoadedSaved.value = true
    } finally {
      isLoadingSaved.value = false
    }
  }

  function renderGradient(gradient: ICallGradientSpec): string {
    const cacheKey = JSON.stringify(gradient)
    const cached = gradientUrlCache.get(cacheKey)
    if (cached) {
      return cached
    }
    const canvas = drawGradientToCanvas(gradient, 1280, 720)
    if (!canvas) {
      return ''
    }
    const dataUrl = canvas.toDataURL('image/png')
    gradientUrlCache.set(cacheKey, dataUrl)
    return dataUrl
  }

  async function getImageUrl(id: string | null): Promise<string | null> {
    if (!id) {
      return null
    }
    const builtIn = CALL_BUILT_IN_BACKGROUNDS.find((item) => item.id === id)
    if (builtIn) {
      if (builtIn.kind === 'gradient' && builtIn.gradient) {
        return renderGradient(builtIn.gradient)
      }
      return builtIn.url ?? null
    }
    const saved = savedBackgrounds.value.find((item) => item.id === id)
    if (saved) {
      return saved.url
    }
    if (!hasLoadedSaved.value) {
      try {
        await loadSavedBackgrounds()
      } catch (_error) {
        void _error
      }
      const loaded = savedBackgrounds.value.find((item) => item.id === id)
      if (loaded) {
        return loaded.url
      }
    }
    return null
  }

  async function addCustomBackground(file: File): Promise<string | null> {
    if (!file.type.startsWith('image/')) {
      return null
    }
    const blob = await downscaleImageToBlob(file, 1920, 0.85)
    const name = `${file.name.replace(/\.[^/.]+$/, '') || 'Background'}.jpg`
    const apiStore = useCallBackgroundApiStore()
    const {uploadFile} = useFileUpload()
    const background = await uploadFile<IToJson<ICallBackgroundResponseInstance>, {background: IToJson<ICallBackgroundResponseInstance>}>({
      targetType: FileUploadTargetType.CallBackground,
      target: {},
      file: new File([blob], name, {type: blob.type || 'image/jpeg'}),
      legacyUpload: () => apiStore.uploadBackground(blob, name),
      mapCompleteResponse: completeResponse => completeResponse.background,
    })
    if (!background.url) {
      return null
    }
    savedBackgrounds.value = [{
      ...background,
      url: background.url,
    }, ...savedBackgrounds.value]
    return background.id
  }

  async function removeCustomBackground(id: string): Promise<void> {
    const apiStore = useCallBackgroundApiStore()
    await apiStore.deleteBackground(id)
    savedBackgrounds.value = savedBackgrounds.value.filter((item) => item.id !== id)
  }

  return {
    savedBackgrounds,
    isLoadingSaved,
    hasLoadedSaved,
    builtInGradients,
    builtInPhotos,
    loadSavedBackgrounds,
    getImageUrl,
    addCustomBackground,
    removeCustomBackground,
  }
})
